import { useCallback, useEffect, useState } from 'react';
import { vehicleTransferService, type PendingTransfersSummary } from '../../../services/vehicleTransferService';
import { APPROVALS_CHANGED_EVENT } from '../../../components/awaitingApprovalsCount';
import { queueHomeRequest } from '../homeRequestQueue';

export interface UsePendingTransfersResult {
  summary: PendingTransfersSummary | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * V3-059 Pending Transfers widget. `enabled` follows the Pending Transfers Tile permission at the
 * call site, so a user without it never sends the request. Goes through the Home request queue,
 * and refetches when an approval changes (approve, reject, cancel, complete).
 */
export function usePendingTransfers(enabled: boolean): UsePendingTransfersResult {
  const [summary, setSummary] = useState<PendingTransfersSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!enabled) {
      setSummary(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setSummary(await queueHomeRequest(() => vehicleTransferService.homeSummary()));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Could not load pending transfers.'));
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onChanged = () => fetchSummary();
    window.addEventListener(APPROVALS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(APPROVALS_CHANGED_EVENT, onChanged);
  }, [enabled, fetchSummary]);

  return { summary, loading, error, refetch: fetchSummary };
}
